import React, { useMemo } from "react";
import {
  BookingVehicleInfoCardProps,
  VehicleSpecsCardProps,
} from "./BookingVehicleInfoCard.props";

interface TechnicalSheetModalContentProps {
  VehicleDetails: BookingVehicleInfoCardProps;
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
  vehicleParameters: any[];
}

const NavButton = ({
  direction,
  onClick,
  disabled,
}: {
  direction: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
}) => (
  <button
    className={`absolute ${
      direction === "prev" ? "left-0" : "right-0"
    } z-10 bg-black text-white p-2 rounded-full top-[70px]`}
    onClick={onClick}
    disabled={disabled}
  >
    {direction === "prev" ? "<" : ">"}
  </button>
);

const ImageItem = ({
  image,
  index,
  isCenter,
}: {
  image: string;
  index: number;
  isCenter: boolean;
}) => (
  <div
    className={`w-full md:w-1/3 p-2 flex items-center justify-center ${
      !isCenter ? "blur" : ""
    }`}
  >
    <img
      src={image}
      className="w-full aspect-video object-contain h-48"
      alt={`Vehicle image ${index + 1}`}
    />
  </div>
);

const useImageNavigation = (
  images: string[],
  currentIndex: number,
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>
) => {
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  return { handlePrev, handleNext };
};

const useVisibleImages = (images: string[], currentIndex: number) => {
  return useMemo(() => {
    if (images.length <= 2) return images;

    let prevIndex = currentIndex - 1;
    let nextIndex = currentIndex + 1;

    if (prevIndex < 0) prevIndex = images.length - 1;

    if (nextIndex >= images.length) nextIndex = 0;

    return [images[prevIndex], images[currentIndex], images[nextIndex]];
  }, [images, currentIndex]);
};

const VehicleImageCarousel = ({
  images,
  currentIndex,
  setCurrentIndex,
}: {
  images: string[];
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const { handlePrev, handleNext } = useImageNavigation(
    images,
    currentIndex,
    setCurrentIndex
  );
  const visibleImages = useVisibleImages(images, currentIndex);
  const isSingleImage = images.length === 1;

  return (
    <aside className="flex flex-row relative">
      {!isSingleImage && (
        <>
          <NavButton
            direction="prev"
            onClick={handlePrev}
            disabled={images.length <= 1}
          />
          <NavButton
            direction="next"
            onClick={handleNext}
            disabled={images.length <= 1}
          />
        </>
      )}
      <div className="flex justify-center w-full">
        {isSingleImage ? (
          <ImageItem image={images[0]} index={0} isCenter={true} />
        ) : (
          <div className="flex w-full justify-center">
            {visibleImages.map((image, index) => (
              <ImageItem
                key={index}
                image={image}
                index={index}
                isCenter={index === 1}
              />
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

const VehicleSpecsCard = ({ iconUrl, name }: VehicleSpecsCardProps) => {
  const isSmallScreen = window.innerWidth <= 768;
  const displayName = name.includes(":") ? name.split(":")[1] : name;

  return (
    <div className="w-full flex flex-col items-center p-4">
      {iconUrl && (
        <img className="h-[22px] w-[32.42px] mb-2" src={iconUrl} alt={name} />
      )}
      <span
        className="font-light text-black text-center"
        style={{ fontSize: "14px", fontFamily: "MBCorpoSText-Regular-Web-new" }}
      >
        {!isSmallScreen ? name : displayName}
      </span>
    </div>
  );
};

const TechnicalSheetHeader = () => (
  <div
    className="box-border ml-[35px]"
    style={{ fontSize: "24px", fontFamily: "MBCorpoAScreen1Cond-Regular-Web" }}
  >
    Technical sheet
  </div>
);

const VehicleNameDisplay = ({ name }: { name: string }) => (
  <div
    className="box-border py-2 md:text-lg text-lg capitalize ml-[32.42px] mt-[30px]"
    style={{
      color: "#1E1E1E",
      fontFamily: "MBCorpoAScreen1Cond-Regular-Web",
      fontSize: "24px",
    }}
  >
    {name}
  </div>
);

const TechnicalSheetModalContent: React.FC<TechnicalSheetModalContentProps> = ({
  VehicleDetails,
  currentIndex,
  setCurrentIndex,
  vehicleParameters,
}) => (
  <div className="box-border">
    <TechnicalSheetHeader />
    {VehicleDetails.vehicleImages.length > 0 && (
      <VehicleImageCarousel
        images={VehicleDetails.vehicleImages}
        currentIndex={currentIndex}
        setCurrentIndex={setCurrentIndex}
      />
    )}
    <VehicleNameDisplay name={VehicleDetails.vehicleName} />
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 px-8 mt-[30px]">
      {vehicleParameters.map((el, index) => (
        <div key={index} className="h-fit w-full text-sm">
          {el ? <VehicleSpecsCard {...el} /> : <div className="w-10 h-10" />}
        </div>
      ))}
    </div>
  </div>
);

export default TechnicalSheetModalContent;
